import { getDatabase, ref, get, update, query, orderByChild, equalTo } from 'firebase/database';
import { AmountHistoryEntry, CoTenantsMap, Lease, LeaseInput, IncomeInterval, LeaseTerms } from '../types/leases';
import { FileAttachment } from '../types/attachments';
import { LeaseObjectType } from '../types/Enums';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { DATE_ONLY_RE, isCoTenantRole, leaseInvolvesTenant, todayStockholm } from '../Helpers/leaseUtils';
import { requireAdmin } from './adminGuard';
import { softDeleteFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';

/** Tombstone entity type for contract files. Shared with the rules tests. */
export const LEASE_CONTRACT_ENTITY_TYPE = 'leaseContract';

/**
 * Object types a lease can be created for. `apartment` and `commercialUnit`
 * are one picker choice in the UI; the server sets the exact type from the
 * unit's kind (units plan D7).
 */
export const SELECTABLE_LEASE_OBJECT_TYPES: LeaseObjectType[] = [
    LeaseObjectType.ParkingSpot,
    LeaseObjectType.StorageUnit,
    LeaseObjectType.Apartment,
    LeaseObjectType.CommercialUnit,
    LeaseObjectType.Other,
];

/** `object` as the leaseApi callable takes it: only the fields its type uses, no label snapshot except for `other`. */
interface LeaseObjectPayload {
    type: LeaseObjectType;
    areaKey?: string;
    key?: string;
    label?: string;
}

interface LeasePayload extends LeaseTerms {
    tenantKey: string;
    buildingKey: string;
    object: LeaseObjectPayload;
    amount: number;
    interval: IncomeInterval;
    startDate: string;
    endDate?: string;
    comment?: string;
    coTenants?: CoTenantsMap;
}

type LeaseApiRequest =
    | ({ action: 'create' } & LeasePayload)
    | ({ action: 'update'; leaseKey: string; amountEffectiveFrom?: string } & LeasePayload)
    | { action: 'end'; leaseKey: string; endDate: string }
    | { action: 'delete'; leaseKey: string };

interface LeaseApiResult {
    success: boolean;
    leaseKey: string;
    buildingKey: string;
}

interface LeaseApiCreateResult extends LeaseApiResult {
    tenantKey: string;
    tenantCreated: boolean;
}

/** Drops undefined and null fields (the RTDB SDK and callable data reject or misread them). */
const stripUndefined = <T extends object>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined && entry !== null) result[key] = entry;
    }
    return result as T;
};

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

/**
 * The server's HttpsError messages are Swedish and shown as they are; codes
 * whose message is the SDK's English default (internal, unavailable, a
 * network failure) get the action's fallback instead.
 */
const callableErrorMessage = (err: unknown, fallback: string): string => {
    if (!(err instanceof Error) || !err.message) return fallback;
    const code = (err as { code?: unknown }).code;
    if (code === 'functions/internal' || code === 'functions/unavailable' || code === 'functions/deadline-exceeded'
        || code === 'functions/unknown') {
        return fallback;
    }
    return err.message;
};

/**
 * Leases at `leases/{leaseKey}` (flat, indexed on tenantKey and buildingKey).
 * Reads need a listed user; every mutation needs an admin (plan D6).
 *
 * create/update/end/delete go through the mf-functions `leaseApi` callable,
 * which validates, writes the lease and the rentable object's
 * `currentLeaseKey` in one update, and tombstones contracts on delete; the
 * rules refuse those writes from clients. Contracts (`contracts/**` plus the
 * lease's `updatedBy`/`updatedWhen`) stay direct client writes.
 */
export class LeasesApi {
    private db = getDatabase();

    getLeasePath(leaseKey: string): string {
        return `leases/${leaseKey}`;
    }

    /** DB path == Storage path for contracts. */
    getContractPath(leaseKey: string, attachmentKey: string): string {
        return `${this.getLeasePath(leaseKey)}/contracts/${attachmentKey}`;
    }

    private stamp(uid: string): { updatedBy: string; updatedWhen: string } {
        return { updatedBy: uid, updatedWhen: formatDateForStorage(new Date()) };
    }

    // MARK: - Parsing

    private parseContracts(data: unknown, leaseKey: string): { [key: string]: FileAttachment } | undefined {
        if (!data || typeof data !== 'object') return undefined;
        const contracts: { [key: string]: FileAttachment } = {};
        for (const [attachmentKey, raw] of Object.entries(data as Record<string, unknown>)) {
            if (!raw || typeof raw !== 'object') continue;
            const a = raw as Record<string, unknown>;
            if (
                typeof a.fileName !== 'string' ||
                typeof a.storagePath !== 'string' ||
                typeof a.fileSize !== 'number' ||
                typeof a.uploadedBy !== 'string' ||
                typeof a.uploadedAt !== 'string'
            ) {
                console.warn(`Invalid contract at ${leaseKey}/${attachmentKey}: missing required fields`);
                continue;
            }
            contracts[attachmentKey] = stripUndefined({
                key: typeof a.key === 'string' ? a.key : attachmentKey,
                fileName: a.fileName,
                fileType: typeof a.fileType === 'string' ? a.fileType : '',
                mimeType: typeof a.mimeType === 'string' ? a.mimeType : '',
                storagePath: a.storagePath,
                fileSize: a.fileSize,
                uploadedBy: a.uploadedBy,
                uploadedAt: a.uploadedAt,
                attachmentType: 'file' as const,
                displayName: optionalString(a.displayName),
            });
        }
        return Object.keys(contracts).length > 0 ? contracts : undefined;
    }

    private parseTerms(d: Record<string, unknown>): LeaseTerms {
        const months = (value: unknown): number | undefined =>
            typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
        return stripUndefined({
            noticeMonths: months(d.noticeMonths),
            renewalMonths: months(d.renewalMonths),
            indexClause: d.indexClause === true ? true : undefined,
            vatRegistered: d.vatRegistered === true ? true : undefined,
        });
    }

    /** Drops malformed entries and a stray entry for the primary tenant (the API never writes one). */
    private parseCoTenants(data: unknown, tenantKey: string): CoTenantsMap | undefined {
        if (!data || typeof data !== 'object') return undefined;
        const coTenants: CoTenantsMap = {};
        for (const [coTenantKey, raw] of Object.entries(data as Record<string, unknown>)) {
            if (!raw || typeof raw !== 'object' || coTenantKey === tenantKey) continue;
            const role = (raw as Record<string, unknown>).role;
            if (!isCoTenantRole(role)) continue;
            coTenants[coTenantKey] = { role };
        }
        return Object.keys(coTenants).length > 0 ? coTenants : undefined;
    }

    private parseAmountHistory(data: unknown): { [entryKey: string]: AmountHistoryEntry } | undefined {
        if (!data || typeof data !== 'object') return undefined;
        const history: { [entryKey: string]: AmountHistoryEntry } = {};
        for (const [entryKey, raw] of Object.entries(data as Record<string, unknown>)) {
            if (!raw || typeof raw !== 'object') continue;
            const e = raw as Record<string, unknown>;
            if (typeof e.from !== 'string' || !DATE_ONLY_RE.test(e.from) || typeof e.amount !== 'number') continue;
            history[entryKey] = { from: e.from, amount: e.amount };
        }
        return Object.keys(history).length > 0 ? history : undefined;
    }

    parseLease(data: unknown, key: string): Lease | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        const interval = d.interval as Record<string, unknown> | undefined;
        const object = d.object as Record<string, unknown> | undefined;
        if (
            typeof d.tenantKey !== 'string' ||
            typeof d.buildingKey !== 'string' ||
            typeof d.amount !== 'number' ||
            typeof d.startDate !== 'string' ||
            !interval || typeof interval.count !== 'number' || (interval.unit !== 'month' && interval.unit !== 'year') ||
            !object || typeof object.type !== 'string' || !(Object.values(LeaseObjectType) as string[]).includes(object.type)
        ) {
            console.warn(`Invalid lease at ${key}: missing required fields`);
            return null;
        }
        return stripUndefined({
            key,
            tenantKey: d.tenantKey,
            coTenants: this.parseCoTenants(d.coTenants, d.tenantKey),
            buildingKey: d.buildingKey,
            object: stripUndefined({
                type: object.type as LeaseObjectType,
                areaKey: optionalString(object.areaKey),
                key: optionalString(object.key),
                label: optionalString(object.label),
            }),
            amount: d.amount,
            interval: { count: interval.count, unit: interval.unit as IncomeInterval['unit'] },
            startDate: d.startDate,
            endDate: optionalString(d.endDate),
            contracts: this.parseContracts(d.contracts, key),
            comment: optionalString(d.comment),
            ...this.parseTerms(d),
            amountHistory: this.parseAmountHistory(d.amountHistory),
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
            updatedBy: optionalString(d.updatedBy),
            updatedWhen: optionalString(d.updatedWhen),
        });
    }

    parseLeases(data: unknown): Lease[] {
        if (!data || typeof data !== 'object') return [];
        const leases: Lease[] = [];
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            const lease = this.parseLease(value, key);
            if (lease) leases.push(lease);
        }
        // Newest period first.
        return leases.sort((a, b) => (a.startDate < b.startDate ? 1 : a.startDate > b.startDate ? -1 : a.key.localeCompare(b.key)));
    }

    // MARK: - Reads

    private async getByChild(child: 'buildingKey' | 'tenantKey', value: string): Promise<Lease[]> {
        const snapshot = await get(query(ref(this.db, 'leases'), orderByChild(child), equalTo(value)));
        return snapshot.exists() ? this.parseLeases(snapshot.val()) : [];
    }

    async getByBuilding(buildingKey: string): Promise<Lease[]> {
        return this.getByChild('buildingKey', buildingKey);
    }

    /**
     * Leases where the tenant is the primary or a co-tenant. `coTenants` is a
     * map keyed by tenant, which RTDB cannot index, so this reads the whole
     * (small) leases root and filters — the same read the tenants list does.
     */
    async getByTenant(tenantKey: string): Promise<Lease[]> {
        return (await this.getAll()).filter(lease => leaseInvolvesTenant(lease, tenantKey));
    }

    async getAll(): Promise<Lease[]> {
        const snapshot = await get(ref(this.db, 'leases'));
        return snapshot.exists() ? this.parseLeases(snapshot.val()) : [];
    }

    async get(leaseKey: string): Promise<Lease | null> {
        const snapshot = await get(ref(this.db, this.getLeasePath(leaseKey)));
        return snapshot.exists() ? this.parseLease(snapshot.val(), leaseKey) : null;
    }

    // MARK: - Mutations (leaseApi callable)

    /**
     * Shapes a form input into the callable payload: trims strings, keeps only
     * the object fields its type uses, drops empty co-tenants and every
     * undefined/null field (callable data must not carry them). Validation is
     * the server's; its Swedish messages reach the UI unchanged.
     */
    private toPayload(input: LeaseInput): LeasePayload {
        const type = input.object?.type;
        const object: LeaseObjectPayload = type === LeaseObjectType.ParkingSpot
            ? stripUndefined({ type, areaKey: optionalString(input.object.areaKey), key: optionalString(input.object.key) })
            : type === LeaseObjectType.Other
                ? stripUndefined({ type, label: optionalString(input.object.label) })
                : stripUndefined({ type, key: optionalString(input.object?.key) });
        const coTenants: CoTenantsMap = {};
        for (const [rawKey, entry] of Object.entries(input.coTenants ?? {})) {
            const key = optionalString(rawKey);
            if (key && entry) coTenants[key] = { role: entry.role };
        }
        const payload: LeasePayload = {
            tenantKey: input.tenantKey?.trim(),
            buildingKey: input.buildingKey?.trim(),
            object,
            amount: input.amount,
            interval: input.interval ? { count: Math.round(input.interval.count), unit: input.interval.unit } : input.interval,
            startDate: input.startDate,
            endDate: optionalString(input.endDate),
            comment: optionalString(input.comment),
            coTenants: Object.keys(coTenants).length > 0 ? coTenants : undefined,
            noticeMonths: input.noticeMonths ?? undefined,
            renewalMonths: input.renewalMonths ?? undefined,
            indexClause: input.indexClause ?? undefined,
            vatRegistered: input.vatRegistered ?? undefined,
        };
        return stripUndefined(payload);
    }

    private async callLeaseApi<T>(request: LeaseApiRequest, fallback: string): Promise<T> {
        // Loaded on use: the functions SDK does not load under jsdom, and many tests import this module.
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const callable = httpsCallable<LeaseApiRequest, T>(getFunctions(undefined, 'europe-north1'), 'leaseApi');
        try {
            const result = await callable(stripUndefined(request));
            return result.data;
        } catch (err) {
            throw new Error(callableErrorMessage(err, fallback));
        }
    }

    /** Creates the lease (the server also sets the object's `currentLeaseKey`) and returns it as stored. */
    async create(input: LeaseInput): Promise<Lease> {
        await requireAdmin(this.db, 'skapa hyresavtal');
        const result = await this.callLeaseApi<LeaseApiCreateResult>(
            { action: 'create', ...this.toPayload(input) },
            'Kunde inte skapa hyresavtal'
        );
        recordActivity('lease.created', { buildingKey: result.buildingKey, targetKey: result.leaseKey });
        const lease = await this.get(result.leaseKey);
        if (!lease) {
            throw new Error('Hyresavtalet skapades men kunde inte läsas in');
        }
        return lease;
    }

    async update(leaseKey: string, input: LeaseInput): Promise<void> {
        await requireAdmin(this.db, 'ändra hyresavtal');
        const result = await this.callLeaseApi<LeaseApiResult>(
            {
                action: 'update',
                leaseKey,
                ...this.toPayload(input),
                amountEffectiveFrom: optionalString(input.amountEffectiveFrom),
            },
            'Kunde inte uppdatera hyresavtal'
        );
        recordActivity('lease.updated', { buildingKey: result.buildingKey, targetKey: leaseKey });
    }

    /** Ends a lease on `endDate` (default today); the server frees or hands over the object in the same write. */
    async end(leaseKey: string, endDate: string = todayStockholm()): Promise<void> {
        await requireAdmin(this.db, 'avsluta hyresavtal');
        const result = await this.callLeaseApi<LeaseApiResult>(
            { action: 'end', leaseKey, endDate },
            'Kunde inte avsluta hyresavtal'
        );
        recordActivity('lease.updated', { buildingKey: result.buildingKey, targetKey: leaseKey });
    }

    /** Removes the lease; the server recomputes the object and tombstones every contract in the same write. */
    async delete(leaseKey: string): Promise<void> {
        await requireAdmin(this.db, 'ta bort hyresavtal');
        const result = await this.callLeaseApi<LeaseApiResult>(
            { action: 'delete', leaseKey },
            'Kunde inte ta bort hyresavtal'
        );
        recordActivity('lease.deleted', { buildingKey: result.buildingKey, targetKey: leaseKey });
    }

    // MARK: - Contracts

    async addContract(leaseKey: string, attachment: FileAttachment): Promise<void> {
        const uid = await requireAdmin(this.db, 'ladda upp avtal');
        const path = this.getLeasePath(leaseKey);
        await update(ref(this.db), {
            [this.getContractPath(leaseKey, attachment.key)]: attachment,
            [`${path}/updatedBy`]: uid,
            [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
        });
    }

    /**
     * Change a contract's display name. The file and its original `fileName`
     * are untouched; an empty name removes the custom name so readers fall
     * back to `fileName`.
     */
    async renameContract(leaseKey: string, attachmentKey: string, displayName: string | undefined): Promise<void> {
        const uid = await requireAdmin(this.db, 'byta namn på avtal');
        const path = this.getLeasePath(leaseKey);
        const contractPath = this.getContractPath(leaseKey, attachmentKey);
        const snapshot = await get(ref(this.db, contractPath));
        if (!snapshot.exists()) throw new Error('Avtalsfilen hittades inte');
        await update(ref(this.db), {
            [`${contractPath}/displayName`]: displayName ?? null,
            [`${path}/updatedBy`]: uid,
            [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
        });
        recordActivity('document.updated', { targetKey: attachmentKey });
    }

    async removeContract(leaseKey: string, attachmentKey: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'ta bort avtal');
        const path = this.getLeasePath(leaseKey);
        await softDeleteFile({
            originalPath: this.getContractPath(leaseKey, attachmentKey),
            entityType: LEASE_CONTRACT_ENTITY_TYPE,
            extraUpdates: {
                [`${path}/updatedBy`]: uid,
                [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
            },
        });
    }
}

export const leasesApi = new LeasesApi();
